import React, { useEffect, useState, Suspense, useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  PetitionPage,
  MobilePetitionPage,
  MobileSignButtonContainer,
  LetterDesktopPetitionPage,
  LetterMobilePetitionPage,
  FormContainerPetitionPage,
  PetitionLogoContainer,
} from "../../styledComponents/PetitionPage.styled";
import { VictoryBox } from "../../styledComponents/HeaderSlider.styled";
import { COLORS } from "../../Variables";
import { PetitionForm } from "./PetitionForm";
import { Helmet } from "react-helmet-async";
import cgoLogo from "../../images/cgo-new-mobile-logo.webp";
import hoLogo from "../../images/logo_ho.webp";
import { Logo } from "../../components/Header";
import { GlobalCtx } from "../../context/GlobalContext";
import { PetitionPageCtx } from "../../context/PetitionContext";
import YouTube from "react-youtube";
import LastFiveSignersList from "./LastFiveSignersList";
import useTrackPageVist from "../../utils/useTrackPageVist";
import Cookies from "js-cookie";

const ExternalPetitionInfo = React.lazy(() =>
  import("../../components/ExternalPetitionInfo")
);
const Footer = React.lazy(() => import("../../components/Footer"));
const Loader = React.lazy(() => import("../../components/Loader"));
const PopupReminderPetitionPage = React.lazy(() =>
  import("../../components/PopupReminderPetitionPage")
);
const SignaturesMeter = React.lazy(() => import("./SignaturesMeter"));
const MobileSignitForm = React.lazy(() => import("./MobileSignitForm"));
const MobilePetitionPopup = React.lazy(() =>
  import("../../components/MobilePetitionPopup/MobilePetitionPopup")
);

const Petition = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [mobileForm, setMobileForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userIsActive, setUserIsActive] = useState(false);
  const [formattedDate, setFormattedDate] = useState(null);
  const [utmCampaign, setUtmCampaign] = useState(null);
  const [sessionReminder, setSessionReminder] = useState(
    sessionStorage.getItem("popupReminder")
  );
  const [hideMobilePopup, setHideMobilePopup] = useState(true);

  const { lang } = useContext(GlobalCtx);
  useTrackPageVist("Petition Page", Cookies.get("signerEmail"));
  const {
    petitionData,
    victoryPetition,
    generateSignatureCount,
    petitionBody,
    featured_video_id,
    lastFiveSigners,
  } = useContext(PetitionPageCtx);

  const { t } = useTranslation();

  const videoOptions = {
    desktops: {
      height: "390",
      width: "640",
    },
    mobiles: {
      height: "240",
      width: "100%",
    },
  };

  const handleResize = () => {
    setWindowWidth(window.innerWidth);
  };

  const checkAndAddUtmCampaign = () => {
    const PARAMS = new URLSearchParams(window.location.search);
    const utm = PARAMS.get("utm_campaign");
    if (utm) {
      setUtmCampaign(utm);
    }
  };

  useEffect(() => {
    checkAndAddUtmCampaign();
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem("popupReminder")) return;
  
    const onMouseLeave = (e) => {
      if (sessionStorage.getItem("popupReminder")) return;
  
      if (e.clientY <= 0) {
        sessionStorage.setItem("popupReminder", "1");
        setSessionReminder("1");
        setUserIsActive(true);
        document.removeEventListener("mouseleave", onMouseLeave);
      }
    };

    document.addEventListener("mouseleave", onMouseLeave);
  return () => document.removeEventListener("mouseleave", onMouseLeave);
}, []);

useEffect(() => {
  if (sessionStorage.getItem("popupReminder")) {
    setHideMobilePopup(true);
    setSessionReminder(sessionStorage.getItem("popupReminder"));
    return;
  }

  let timerId = null;

  const handleStop = () => {
    if (sessionStorage.getItem("popupReminder")) return;

    sessionStorage.setItem("popupReminder", "1");
    setSessionReminder("1");
    // temp change
    setHideMobilePopup(true);

    window.removeEventListener("scroll", onScroll, { passive: true });
    window.removeEventListener("touchstart", onTouchStart, { passive: true });
    window.removeEventListener("touchend", onTouchEnd, { passive: true });
  };

  const onScroll = () => {
    setHideMobilePopup(true);

    if (timerId !== null) {
      window.clearTimeout(timerId);
    }
    timerId = window.setTimeout(() => {
      handleStop();
      timerId = null;
    }, 5000);
  };

  const onTouchStart = () => {
    setHideMobilePopup(true);
    if (timerId !== null) window.clearTimeout(timerId);
  };

  const onTouchEnd = () => {
    if (timerId !== null) window.clearTimeout(timerId);
    timerId = window.setTimeout(() => {
      handleStop();
      timerId = null;
    }, 5000);
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("touchstart", onTouchStart, { passive: true });
  window.addEventListener("touchend", onTouchEnd, { passive: true });

  return () => {
    if (timerId !== null) window.clearTimeout(timerId);
    window.removeEventListener("scroll", onScroll);
    window.removeEventListener("touchstart", onTouchStart);
    window.removeEventListener("touchend", onTouchEnd);
  };
}, []);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
  }, []);

  const {
    title,
    featured_image,
    authorImg,
    owner,
    start_date,
    signature_count,
    goal,
    padding,
    recipient_name,
    teaser,
    id,
    status,
    petition_msg,
    petition_msg_subj,
    personal_msg,
    external,
    thankyou_page,
    after_sign,
    inverted_flow,
    sign_button_text,
    feedback_questions,
    drupal_nid,
    cluster_count,
    allow_transfer_default,
    partner,
    recent_signatures_box,
  } = petitionData;

  const addBreaksBetweenParagraphs = (html) => {
    return html?.replace(
      /<\/(p|h1|h1|li)>\s*<(p|h1|h2|li)>/g,
      "</$1><br/><$2>"
    );
  };

  const sanitizedBodyData = () => ({
    __html: addBreaksBetweenParagraphs(petitionBody),
  });

  const sanitizedTeaserData = () => ({
    __html: addBreaksBetweenParagraphs(teaser),
  });

  const sanitizedLetterContent = () => ({
    __html: petition_msg,
  });

  const sanitizedLetterSubject = () => ({
    __html: petition_msg_subj,
  });

  const LogoStyle = {
    marginTop: "10px",
    left: 0,
  };

  const dateFormat = () => {
    if (lang !== "en-us") {
      setFormattedDate(start_date?.replace(/-/g, "/"));
    } else {
      setFormattedDate(start_date);
    }
  };

  const checkIfVictoryIsVFP = (object) => {
    for (let key in object) {
      if (object[key] === "vfp") {
        return true;
      }
    }
    return false;
  };

  // do not render petition form if it is a victory petition unless it has a Victory & Sign status
  const shouldRenderPetitionForm =
    !victoryPetition || victoryPetition?.code === "vas";
  const shouldRenderLastSigners =
    !victoryPetition && lastFiveSigners.length !== 0 && !recent_signatures_box;

  useEffect(() => {
    dateFormat();
  }, [petitionData]);

  const startedThisPetitionTextForHU = () => (
    <span>
      A petíció elindítója: {owner?.first_name} {owner?.last_name} Címzettje:{" "}
      {recipient_name} - {formattedDate}
    </span>
  );

  if (windowWidth > 768) {
    return (
      <>
        <Helmet>
          <title data-rh="true">{title}</title>
          <link
            data-rh="true"
            rel="icon"
            href={
              lang !== "hazteoir"
                ? "https://citizengo.org/favicon.ico"
                : "https://citizengo.org/ho_favicon.ico"
            }
          />
          <meta property="og:description" content={title} data-rf="true" />
          <meta property="og:title" content={title} data-rf="true" />
          <meta property="og:image" content={featured_image} data-rf="true" />
          <meta
            property="og:url"
            content={window.location.href}
            data-rf="true"
          />
          <meta property="og:type" content="article" data-rf="true" />
          <meta
            property="og:site_name"
            content={lang === "hazteoir" ? "Hazte Oír" : "CitizenGO"}
            data-rf="true"
          />
          <meta property="og:locale" content={lang} data-rf="true" />
          <meta name="twitter:card" content="summary" data-rf="true" />
          <meta name="twitter:title" content={title} data-rf="true" />
          <meta name="twitter:description" content={title} data-rf="true" />
          <meta name="twitter:image" content={featured_image} data-rf="true" />
          <meta name="twitter:site" content="@CitizenGO" data-rf="true" />
          <meta
            name="twitter:url"
            content={window.location.href}
            data-rf="true"
          />
          <link
            rel="cannonical"
            href={`https://citizengo.org/${lang}/${id}`}
            data-rh="true"
          />
          <meta data-rh="true" property="og:image:width" content="1600" />
          <meta data-rh="true" property="og:image:height" content="900" />
          <link
            data-rh="true"
            rel="preload"
            href={featured_image?.replace("https:", "")}
            as="image"
            media="(max-width: 768px)"
            fetchpriority="high"
          />
          <link
            data-rh="true"
            rel="preload"
            href={featured_image?.replace("https:", "")}
            as="image"
            media="(min-width: 769px)"
            fetchpriority="high"
          />
        </Helmet>
        <Suspense fallback={<Loader />}>
          <PetitionLogoContainer>
            {lang === "hazteoir" ? (
              <Logo
                onClick={() => (window.location.href = "/")}
                style={LogoStyle}
                src={hoLogo}
                alt="Hazteoir logo"
              />
            ) : (
              <Logo
                onClick={() => (window.location.href = "/")}
                style={LogoStyle}
                src={cgoLogo}
                alt="CitizenGO logo"
              />
            )}
          </PetitionLogoContainer>
          {featured_image && (
            <PetitionPage
              maxWidth={
                checkIfVictoryIsVFP(victoryPetition) ? "1500px" : "960px"
              }
              content={
                checkIfVictoryIsVFP(victoryPetition) ? "center" : "normal"
              }
              fullTitle={
                checkIfVictoryIsVFP(victoryPetition) ? "full" : "normal"
              }
              id="petition_page"
            >
              {loading ? <Loader /> : null}
              {!victoryPetition && userIsActive && (
                <PopupReminderPetitionPage
                  trigger={userIsActive}
                  setTrigger={setUserIsActive}
                  title={title}
                  signatures_count={signature_count}
                  lang={lang}
                  owner={owner?.first_name ? owner.first_name : "author"}
                  lastName={owner?.last_name ? owner.last_name : ""}
                  setSessionReminder={setSessionReminder}
                />
              )}
              <h1 id="petition_title">{title}</h1>
              <div className="petition_content">
                <div className="left_side">
                  <span className="petition_image">
                    {victoryPetition && (
                      <VictoryBox top="20px" width="15%">
                        {t("slider.red_victory")}
                      </VictoryBox>
                    )}
                    {featured_video_id ? (
                      <YouTube
                        videoId={featured_video_id}
                        opts={videoOptions.desktops}
                      />
                    ) : (
                      <img src={`${featured_image}`} alt={title} />
                    )}
                  </span>
                  {external && !partner && <ExternalPetitionInfo />}
                  <div className="author">
                    <img
                      src={
                        !owner?.photo
                          ? "https://citizengo.org/favicon.ico"
                          : owner?.photo
                      }
                      alt="petition author image"
                    />
                    {lang === "hu" ? (
                      startedThisPetitionTextForHU()
                    ) : (
                      <span>
                        {owner?.first_name} {owner?.last_name}
                        {victoryPetition?.code !== "vfp"
                          ? t("petition_page.started_this_petition") +
                            " " +
                            recipient_name +
                            " - " +
                            formattedDate
                          : " - " + formattedDate}
                      </span>
                    )}
                  </div>
                  <p
                    id="petition_text"
                    dangerouslySetInnerHTML={sanitizedBodyData()}
                  />
                </div>
                {/* if petition.code is vfp don't render .right_side */}
                {checkIfVictoryIsVFP(victoryPetition) ? null : (
                  <div className="right_side">
                    <div className="right_side_title">
                      <span>
                        <p>
                          {/* {((signature_count + padding) >= (cluster_count + padding) ? signature_count + padding : cluster_count + padding).toLocaleString(lang === 'hazteoir' ? 'de-DE' : 'en-US')}{' '} */}
                          {generateSignatureCount()}{" "}
                          {t("petition_page.counter")}.
                        </p>
                        <p>
                          {status === null &&
                            t("petition_page.goal", {
                              goal: goal.toLocaleString(
                                lang === "hazteoir" ? "de-DE" : "en-US"
                              ),
                            })}
                        </p>
                      </span>
                      <SignaturesMeter
                        lang={lang}
                        goal={goal}
                        signatures_count={signature_count}
                        padding={padding}
                        cluster_count={cluster_count}
                      />
                    </div>
                    {shouldRenderPetitionForm && (
                      <FormContainerPetitionPage lang={lang} id="petition_form">
                        {/* form styled in PetitionPage.styled.js */}
                        <PetitionForm
                          id={id}
                          lang={lang}
                          nid={drupal_nid ? drupal_nid : null}
                          goal={goal}
                          title={title}
                          personal_msg={personal_msg}
                          after_sign={after_sign}
                          thankyou_page={thankyou_page}
                          external={external}
                          inverted_flow={inverted_flow}
                          sign_button_text={sign_button_text}
                          feedback_questions={feedback_questions}
                          utmCampaign={utmCampaign}
                          owner={owner}
                          allow_transfer_default={allow_transfer_default}
                        />
                      </FormContainerPetitionPage>
                    )}
                    {shouldRenderLastSigners && <LastFiveSignersList />}
                    <LetterDesktopPetitionPage className="right_side_letter">
                      <p
                        className="letter_title"
                        dangerouslySetInnerHTML={sanitizedLetterSubject()}
                      />
                      <p
                        className="letter_content"
                        dangerouslySetInnerHTML={sanitizedTeaserData()}
                      />
                    </LetterDesktopPetitionPage>
                  </div>
                )}
              </div>
            </PetitionPage>
          )}
          <Footer lang={lang} />
        </Suspense>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title data-rh="true">{title}</title>
        <link
          data-rh="true"
          rel="icon"
          href={
            lang !== "hazteoir"
              ? "https://citizengo.org/favicon.ico"
              : "https://citizengo.org/ho_favicon.ico"
          }
        />
        <meta property="og:description" content={title} data-rf="true" />
        <meta property="og:title" content={title} data-rf="true" />
        <meta property="og:image" content={featured_image} data-rf="true" />
        <meta property="og:url" content={window.location.href} data-rf="true" />
        <meta property="og:type" content="article" data-rf="true" />
        <meta
          property="og:site_name"
          content={lang === "hazteoir" ? "Hazte Oír" : "CitizenGO"}
          data-rf="true"
        />
        <meta property="og:locale" content={lang} data-rf="true" />
        <meta name="twitter:card" content="summary" data-rf="true" />
        <meta name="twitter:title" content={title} data-rf="true" />
        <meta name="twitter:description" content={title} data-rf="true" />
        <meta name="twitter:image" content={featured_image} data-rf="true" />
        <meta name="twitter:site" content="@CitizenGO" data-rf="true" />
        <meta
          name="twitter:url"
          content={window.location.href}
          data-rf="true"
        />
        <link
          rel="cannonical"
          href={`https://citizengo.org/${lang}/${id}`}
          data-rh="true"
        />
        <meta data-rh="true" property="og:image:width" content="1600" />
        <meta data-rh="true" property="og:image:height" content="900" />
        <link
          data-rh="true"
          rel="preload"
          href={featured_image?.replace("https:", "")}
          as="image"
          media="(max-width: 768px)"
          fetchpriority="high"
        />
        <link
          data-rh="true"
          rel="preload"
          href={featured_image?.replace("https:", "")}
          as="image"
          media="(min-width: 769px)"
          fetchpriority="high"
        />
      </Helmet>
      {featured_image && (
        <>
          <Suspense fallback={<Loader />}>
            {!hideMobilePopup && (
              <MobilePetitionPopup
                setMobileForm={setMobileForm}
                setHideMobilePopup={setHideMobilePopup}
                owner={owner?.first_name ? owner.first_name : "author"}
                lastName={owner?.last_name ? owner.last_name : ""}
                title={title}
                lang={lang}
                signature_count={signature_count}
              />
            )}
            <MobilePetitionPage
              style={{ height: mobileForm ? "100vh" : null }}
              id="petition_page_m"
            >
              {lang === "hazteoir" ? (
                <Logo
                  onClick={() => (window.location.href = "/")}
                  style={LogoStyle}
                  src={hoLogo}
                  alt="Hazteoir logo"
                />
              ) : (
                <Logo
                  onClick={() => (window.location.href = "/")}
                  style={LogoStyle}
                  src={cgoLogo}
                  alt="CitizenGO logo"
                />
              )}
              <h1 id="petition_title_m">{title}</h1>
              {victoryPetition && (
                <VictoryBox
                  top="20px"
                  left="0"
                  radius="0 5px 5px 0"
                  width="30%"
                  display="none"
                >
                  {t("slider.red_victory")}
                </VictoryBox>
              )}
              {featured_video_id ? (
                <YouTube
                  videoId={featured_video_id}
                  opts={videoOptions.mobiles}
                />
              ) : (
                <img src={`${featured_image}`} alt={title} />
              )}
              {external && !partner && <ExternalPetitionInfo />}
              {!victoryPetition && (
                <>
                  <SignaturesMeter
                    lang={lang}
                    goal={goal}
                    signatures_count={signature_count}
                    padding={padding}
                    cluster_count={cluster_count}
                  />
                  <span className="mobile_counter">
                    <p>
                      {generateSignatureCount()} {t("petition_page.counter")}.
                    </p>
                    <p>
                      {/* en-US uses colon, and de-DE uses dot */}
                      {status === null &&
                        t("petition_page.goal", {
                          goal: goal.toLocaleString(
                            lang === "hazteoir" ? "de-DE" : "en-US"
                          ),
                        })}
                    </p>
                  </span>
                </>
              )}
              <div className="author">
                {owner?.photo === undefined ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 448 512"
                    fill="#adacad"
                    className="no_user_image"
                  >
                    <path d="M224 256A128 128 0 1 0 224 0a128 128 0 1 0 0 256zm-45.7 48C79.8 304 0 383.8 0 482.3C0 498.7 13.3 512 29.7 512H418.3c16.4 0 29.7-13.3 29.7-29.7C448 383.8 368.2 304 269.7 304H178.3z" />
                  </svg>
                ) : (
                  <img src={owner?.photo} alt="petition author image" />
                )}
                <span>
                  {owner?.first_name} {owner?.last_name}
                  {victoryPetition?.code !== "vfp"
                    ? t("petition_page.started_this_petition") +
                      " " +
                      recipient_name +
                      " - " +
                      formattedDate
                    : " - " + formattedDate}
                </span>
              </div>
              <p
                className="mobile_petition_content"
                dangerouslySetInnerHTML={sanitizedBodyData()}
              />
              <LetterMobilePetitionPage className="right_side_letter">
                <p
                  className="letter_title"
                  dangerouslySetInnerHTML={sanitizedLetterSubject()}
                />
                <p
                  className="letter_content"
                  dangerouslySetInnerHTML={sanitizedTeaserData()}
                />
              </LetterMobilePetitionPage>
              {shouldRenderPetitionForm && (
                <MobileSignButtonContainer>
                  <button
                    className="mobile_sign_button_1"
                    aria-label="sign"
                    style={{
                      backgroundColor:
                        lang === "hazteoir"
                          ? `${COLORS.hoOrange}`
                          : `${COLORS.cgoBlue}`,
                    }}
                    onClick={() => setMobileForm(true)}
                  >
                    {sign_button_text
                      ? sign_button_text
                      : t("petition_form.sign_this_petition")}
                  </button>
                </MobileSignButtonContainer>
              )}
            </MobilePetitionPage>
            {shouldRenderPetitionForm && (
              <MobileSignitForm
                nid={drupal_nid ? drupal_nid : null}
                id={id}
                goal={goal}
                title={title}
                trigger={mobileForm}
                setTrigger={setMobileForm}
                personal_msg={personal_msg}
                lang={lang}
                after_sign={after_sign}
                thankyou_page={thankyou_page}
                external={external}
                inverted_flow={inverted_flow}
                sign_button_text={sign_button_text}
                feedback_questions={feedback_questions}
                utmCampaign={utmCampaign}
                owner={owner}
                allow_transfer_default={allow_transfer_default}
              />
            )}
            <Footer lang={lang} />
          </Suspense>
        </>
      )}
    </>
  );
};

export default Petition;
